import React, { useState } from 'react';
import { MapPin, Instagram, Phone, Mail, Send } from 'lucide-react';
import { track } from '../lib/meta';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { contactInfo, serviceTypes, problemTypes } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    deviceType: '',
    problemDescription: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === "phone") {
    const numbers = value.replace(/\D/g, "").slice(0, 11);

    setFormData((prev) => ({
      ...prev,
      phone: numbers
    }));

    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: value
  }));
};

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validação básica
    if (!formData.name || !formData.phone || !formData.serviceType || !formData.problemDescription) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepara os dados para enviar ao backend
      const requestData = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone,
        service_type: formData.serviceType,
        device_type: formData.deviceType || null,
        problem_description: formData.problemDescription
      };

      // Envia para a API
      const response = await axios.post(`${API}/solicitations/`, requestData);
      
      if (response.status >= 200 && response.status < 300) {
        window.fbq('track', 'Lead', {
          content_name: formData.serviceType,
          content_category: formData.deviceType,
          currency: 'BRL',
          value: 1
        });

        toast.success('Solicitação enviada com sucesso! Entraremos em contato em breve.');

        setFormData({
          name: '',
          email: '',
          phone: '',
          serviceType: '',
          deviceType: '',
          problemDescription: ''
        });
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      
      let errorMessage = 'Ocorreu um erro inesperado. Tente novamente.';
      
      if (error.response) {
        // Erro da API - pode ser objeto ou string
        const detail = error.response.data?.detail;
        
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Erros de validação do Pydantic
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (detail && typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        } else {
          errorMessage = 'Não foi possível enviar sua solicitação';
        }
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white">
            Entre em Contato
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Preencha o formulário abaixo ou entre em contato direto pelo WhatsApp
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-slate-900 border border-blue-900/30 rounded-2xl p-8 space-y-6">
              <h2 className="text-2xl font-bold text-white mb-6">Informações de Contato</h2>
              
              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(contactInfo.message)}`}
                onClick={() => {
                  window.fbq('track', 'Contact');
                }}
                rel="noopener noreferrer"
                className="flex items-start space-x-4 p-4 rounded-xl bg-blue-900/20 border border-blue-700/30 hover:border-blue-600/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-800/50 transition-colors">
                  <Phone size={24} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                  <p className="text-gray-400 text-sm">67 98155-6252</p>
                  <p className="text-blue-400 text-sm mt-1">Clique para conversar →</p>
                </div>
              </a>

              <a
                href={`https://instagram.com/${contactInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800 border border-blue-900/30 hover:border-blue-700/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-800/50 transition-colors">
                  <Instagram size={24} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Instagram</h3>
                  <p className="text-gray-400 text-sm">@{contactInfo.instagram}</p>
                  <p className="text-blue-400 text-sm mt-1">Siga-nos →</p>
                </div>
              </a>

              <div className="flex items-start space-x-4 p-4 rounded-xl bg-slate-800 border border-blue-900/30">
                <div className="w-12 h-12 bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Localização</h3>
                  <p className="text-gray-400 text-sm">{contactInfo.location}</p>
                </div>
              </div>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-gradient-to-br from-blue-900/40 to-blue-950/40 border border-blue-700/50 rounded-2xl p-8 space-y-4">
              <h3 className="text-xl font-bold text-white">Atendimento Rápido</h3>
              <p className="text-gray-300 text-sm">
                Prefere falar diretamente? Clique no botão abaixo e seja direcionado ao WhatsApp com uma mensagem pronta!
              </p>
              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(contactInfo.message)}`}
                onClick={() => {
                  if (window.fbq) {
                    window.fbq('track', 'Contact');
                  }
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold text-center hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg shadow-blue-500/30"
              >
                Abrir WhatsApp
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-900 border border-blue-900/30 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Formulário de Solicitação</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-300">
                  Nome Completo <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  className="bg-slate-800 border-blue-900/30 text-white placeholder:text-gray-500 focus:border-blue-600"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">
                  E-mail
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  className="bg-slate-800 border-blue-900/30 text-white placeholder:text-gray-500 focus:border-blue-600"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-300">
                  Telefone/WhatsApp <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(67) 98155-6252"
                  className="bg-slate-800 border-blue-900/30 text-white placeholder:text-gray-500 focus:border-blue-600"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="serviceType" className="text-gray-300">
                  Tipo de Dispositivo <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(value) => handleSelectChange('serviceType', value)}
                  required
                >
                  <SelectTrigger className="bg-slate-800 border-blue-900/30 text-white focus:border-blue-600">
                    <SelectValue placeholder="Selecione o dispositivo" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-blue-900/30">
                    {serviceTypes.map((type) => (
                      <SelectItem key={type} value={type} className="text-white hover:bg-blue-900/30">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deviceType" className="text-gray-300">
                  Tipo de Problema
                </Label>
                <Select
                  value={formData.deviceType}
                  onValueChange={(value) => handleSelectChange('deviceType', value)}
                >
                  <SelectTrigger className="bg-slate-800 border-blue-900/30 text-white focus:border-blue-600">
                    <SelectValue placeholder="Selecione o tipo de problema" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-blue-900/30">
                    {problemTypes.map((type) => (
                      <SelectItem key={type} value={type} className="text-white hover:bg-blue-900/30">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="problemDescription" className="text-gray-300">
                  Descrição do Problema <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  id="problemDescription"
                  name="problemDescription"
                  value={formData.problemDescription}
                  onChange={handleChange}
                  placeholder="Descreva detalhadamente o problema do seu dispositivo..."
                  rows={5}
                  className="bg-slate-800 border-blue-900/30 text-white placeholder:text-gray-500 focus:border-blue-600 resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <span className="animate-spin">⏳</span>
                    <span>Enviando...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <Send size={20} />
                    <span>Enviar Solicitação</span>
                  </span>
                )}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                Ao enviar, você concorda que entraremos em contato através dos dados fornecidos
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
